import type {
  ApprovePromotionRequest,
  FormattedPromotion,
  PromotionApprovalsListType
} from '@library/types/promotions';
import {
  PlayerName,
  PlayerProfile,
  PlayerSubheader
} from '@library/components/player';
import { useCallback, useMemo, useState } from 'react';
import { ApprovePromotionModal } from '@bo-components/promotions/approvals/ApprovePromotionModal';
import { DateTime } from 'luxon';
import { PromotionPendingApprovalActionsKebabMenu } from '@library/components/menus';
import { formatWithTimezoneChange } from '@library/utils/dateTime';
import { getPromotionAdminLink } from '@library/utils/promotions';
import { useApprovePromotionMutation } from '@bo-services/promotionsApi';

interface PromotionApprovalsRowProps {
  promotion: FormattedPromotion;
  listType: PromotionApprovalsListType;
}

export const PromotionApprovalsRow = ({
  promotion,
  listType
}: PromotionApprovalsRowProps) => {
  const [approvePromotionModalOpen, setApprovePromotionModalOpen] =
    useState(false);

  const [isApprovingPromotion, setIsApprovingPromotion] = useState(false);

  const [approvePromotion] = useApprovePromotionMutation();

  const {
    id,
    attributes: {
      name,
      type,
      eligibleUserListCount,
      maxDollarValue,
      payoutType,
      createdAt,
      availableAt,
      approvedAt,
      approvedBy,
      reimbursementType
    }
  } = promotion;

  const payoutAmount: string = useMemo(() => {
    if (maxDollarValue && eligibleUserListCount) {
      return `$${new Intl.NumberFormat().format(
        maxDollarValue * eligibleUserListCount
      )}`;
    }
    return '-';
  }, [maxDollarValue, eligibleUserListCount]);

  const formattedMemberCount: string = new Intl.NumberFormat().format(
    eligibleUserListCount
  );

  const formattedApprovedAt = approvedAt
    ? formatWithTimezoneChange(DateTime.fromISO(approvedAt), 'MMM d, h:mma')
    : '-';

  const formattedCreatedAt = formatWithTimezoneChange(
    DateTime.fromISO(createdAt),
    'MMM d, h:mma'
  );

  const formattedAvailableAt = formatWithTimezoneChange(
    DateTime.fromISO(availableAt),
    'MMM d, h:mma'
  );

  const payoutTypeDisplay = useMemo(() => {
    if (type === 'ProtectedPlay') {
      return (
        reimbursementType.charAt(0).toUpperCase() + reimbursementType.slice(1)
      );
    }
    if (!payoutType) {
      return 'Unknown Type';
    }
    return payoutType.charAt(0).toUpperCase() + payoutType.slice(1);
  }, [payoutType, reimbursementType, type]);

  const handleConfirmPromotionApprove = useCallback(
    async (approveReq: ApprovePromotionRequest) => {
      setIsApprovingPromotion(true);
      await approvePromotion({ promotionId: approveReq.promotionId });
      setApprovePromotionModalOpen(false);
    },
    [approvePromotion]
  );

  const handleCancelModal = useCallback(() => {
    setApprovePromotionModalOpen(false);
  }, []);

  const handleShowApprovalModal = useCallback(() => {
    setApprovePromotionModalOpen(true);
  }, []);

  const handleViewPromotionClick = () => {
    // TODO: ENGAG-??? navigate to promotion view in HQ
    const url = getPromotionAdminLink(id, type);
    window.open(url, '_blank');
  };

  return (
    <>
      <div
        id="promotionPendingApprovalRowContainer"
        className="border-soFresh-140 text-soClean-110 hover:bg-soFresh-120 group/user text-2xs relative flex items-center border-b xl:text-xs"
        key={id}
      >
        {/* Promotion ID */}
        <div className="flex w-[10%] items-center p-3">{id}</div>

        {/* Promotion Name & Type */}
        <PlayerProfile
          className="flex w-2/6 items-center p-3"
          name={name}
          subheader={type}
        >
          <div id="promoNameAndShortDescContainer" className="flex flex-col">
            <PlayerName className="font-heebo text-soClean-100 block text-sm font-medium capitalize tracking-tight" />
            <PlayerSubheader className="text-soClean-170 font-heebo box-content line-clamp-1 overflow-hidden text-ellipsis text-xs font-medium tracking-tight" />
          </div>
        </PlayerProfile>

        {/* Member Count */}
        <PlayerProfile
          className="flex w-1/6 items-center p-3"
          name={eligibleUserListCount ? formattedMemberCount : 'None'}
          subheader={eligibleUserListCount ? `Members` : ''}
        >
          <div className="flex flex-col">
            <PlayerName className="font-heebo text-soClean-100 block text-sm font-medium capitalize tracking-tight" />
            <PlayerSubheader className="text-soClean-170 font-heebo block text-xs font-medium tracking-tight" />
          </div>
        </PlayerProfile>

        {/* Payout Amount & Type */}
        <PlayerProfile
          className="flex w-1/6 items-center p-3"
          name={payoutAmount}
          subheader={payoutTypeDisplay}
        >
          <div className="flex flex-col">
            <PlayerName className="font-heebo text-soClean-100 block text-sm font-medium capitalize tracking-tight" />
            <PlayerSubheader className="text-soClean-170 font-heebo block text-xs font-medium tracking-tight" />
          </div>
        </PlayerProfile>

        {/* Created At */}
        <div className="flex w-1/6 items-center p-3">{formattedCreatedAt}</div>

        {/* Available At Column on Pending & Approved Tables, Expired At Column on Expired Table  */}
        <div className="flex w-1/6 items-center p-3">
          {formattedAvailableAt}
        </div>

        {listType === 'approved' && (
          <PlayerProfile
            className="flex w-2/6 items-center p-3"
            name={approvedBy || 'Name not found'}
          >
            <div className="flex flex-col">
              <PlayerName className="font-heebo text-soClean-100 block text-sm font-medium tracking-tight" />
            </div>
          </PlayerProfile>
        )}

        {listType === 'approved' && (
          <div className="flex w-1/6 items-center p-3">
            {formattedApprovedAt}
          </div>
        )}

        {/* Actions */}
        {listType === 'pending' && (
          <div className="absolute right-2 z-10">
            <PromotionPendingApprovalActionsKebabMenu
              onShowApprovalModal={handleShowApprovalModal}
              onViewPromotionClick={handleViewPromotionClick}
              visibleMenuItems={{
                approve: true,
                viewPromotion: true,
                viewPromoUsers: false
              }}
            />
          </div>
        )}
      </div>

      <ApprovePromotionModal
        isOpen={approvePromotionModalOpen}
        onCancelModal={handleCancelModal}
        onConfirm={handleConfirmPromotionApprove}
        promotionId={Number(id)}
        memberCount={formattedMemberCount}
        payoutAmount={payoutAmount}
        isLoading={isApprovingPromotion}
      />
    </>
  );
};
